import React, { Fragment, useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  useModuleShowcase,
  useUpdateModuleShowcase,
} from "../../moduleServices";
import Container from "@material-ui/core/Container";
import List from "@material-ui/core/List";
import { Box, ListItem, ListItemText } from "@material-ui/core";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";

import Avatar from "@material-ui/core/Avatar";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import { notBlank } from "../../../../util/ext";
import { logOut } from "../../../../auth/authActions";
import { useDispatch } from "react-redux";
import { findIndex, move, pluck, propEq, reject } from "ramda";
import ShowcaseItemActions from "./ShowcaseItemActions";
import AddToShowcaseMenu from "./AddToShowcaseMenu";
import MediaDialog from "../../../common/MediaDialog";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import { useSnackAlert } from "../../../../common/use-snackAlert";

const useStyles = makeStyles(theme => ({
  thumbnail: {
    width: 45,
    height: 45,
  },

  showcaseList: {
    maxHeight: 750,
    overflow: "auto",
  },

  description: {
    padding: "0",
  },

  mediaTitle: {
    maxWidth: 450,
  },

  body1: {
    fontStyle: "italic",
    fontSize: "1.4rem",
    lineHeight: "1.8rem",
    color: theme.palette.primary.main,
  },
}));

const directions = {
  up: "up",
  down: "down",
  top: "top",
  bottom: "bottom",
};

const ModuleShowcaseDialog = ({ open, moduleId, onCloseClicked }) => {
  const classes = useStyles();
  const reduxDispatch = useDispatch();
  const [showcaseItems, setShowcaseItems] = useState([]);

  const { SnackAlert, showAlert } = useSnackAlert();

  const results = useModuleShowcase(moduleId);
  useEffect(() => {
    if (results.isError) {
      if (results.error.status === 401) {
        reduxDispatch(logOut());
      }
    }

    if (results.isSuccess) {
      setShowcaseItems(results.data);
    }
  }, [
    results.isError,
    results.error,
    results.isSuccess,
    results.data,
    reduxDispatch,
  ]);

  const handleChangeOrder = useCallback(
    (id, direction) => {
      const itemIndex = findIndex(propEq("id", id), showcaseItems);

      if (direction === directions.top) {
        setShowcaseItems(prev => move(itemIndex, 0, prev));
      } else if (direction === directions.bottom) {
        setShowcaseItems(prev => move(itemIndex, -1, prev));
      } else if (direction === directions.up) {
        if (itemIndex > 0) {
          setShowcaseItems(prev => move(itemIndex, itemIndex - 1, prev));
        }
      } else if (direction === directions.down) {
        if (itemIndex < showcaseItems.length - 1) {
          setShowcaseItems(prev => move(itemIndex, itemIndex + 1, prev));
        }
      }
    },
    [showcaseItems],
  );

  const handleDeleteItem = useCallback(id => {
    setShowcaseItems(prev => reject(propEq("id", id), prev));
  }, []);

  const handleAddItems = useCallback(items => {
    setShowcaseItems(items);
  }, []);

  const updateModuleShowcase = useUpdateModuleShowcase();
  const handleSave = useCallback(async () => {
    const mediaIds = pluck("id", showcaseItems);
    try {
      await updateModuleShowcase.mutateAsync({
        resourceIds: { id: moduleId },
        data: { ids: mediaIds },
      });
      showAlert("success", ["Module Showcase updated."]);
    } catch (err) {
      showAlert("error", [err.body.message]);
    }
  }, [moduleId, showcaseItems, updateModuleShowcase, showAlert]);

  const handleClose = useCallback(() => {
    onCloseClicked();
  }, [onCloseClicked]);

  return (
    <MediaDialog
      open={open}
      maxWidth="md"
      title="Module Showcase"
      handleClose={handleClose}
      loading={results.isLoading}
      content={
        <Container classes={{ root: classes.showcaseList }} maxWidth={false}>
          {results.isSuccess && notBlank(showcaseItems) && (
            <Fragment>
              <SnackAlert />
              <AddToShowcaseMenu
                moduleId={moduleId}
                showcaseItems={showcaseItems}
                onAddClicked={handleAddItems}
              />

              <List dense={true}>
                {showcaseItems.map(media => (
                  <ListItem key={media.id}>
                    <ListItemIcon>
                      <Avatar
                        variant="rounded"
                        alt={media.title || media.name}
                        src={`${media.thumbnailUrl}&tc${Date.now()}`}
                        className={classes.thumbnail}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          classes={{ root: classes.mediaTitle }}
                          noWrap
                        >
                          {media.title || media.name}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <ShowcaseItemActions
                        onTop={() =>
                          handleChangeOrder(media.id, directions.top)
                        }
                        onUp={() => handleChangeOrder(media.id, directions.up)}
                        onDown={() =>
                          handleChangeOrder(media.id, directions.down)
                        }
                        onBottom={() =>
                          handleChangeOrder(media.id, directions.bottom)
                        }
                        onDelete={() => handleDeleteItem(media.id)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Fragment>
          )}
          {results.isError && (
            <Box
              width={"100%"}
              height={65}
              ml={"auto"}
              mr={"auto"}
              mt={12}
              textAlign="center"
            >
              <Typography
                component="div"
                variant="body1"
                classes={{ body1: classes.body1 }}
              >
                {results.error.status === 404
                  ? "No Showcase has been created for this Module."
                  : results.error.body.message}
              </Typography>
            </Box>
          )}
        </Container>
      }
      actions={
        <Fragment>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={results.isLoading || results.isError}
          >
            Save
          </Button>
          <Button variant="contained" color="secondary" onClick={handleClose}>
            Cancel
          </Button>
        </Fragment>
      }
    />
  );
};

export default ModuleShowcaseDialog;
